import { Link } from "react-router-dom";
import {
  ArrowRight,
  Star,
  Truck,
  Shield,
  Clock,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import { useState, useEffect } from "react";
import assets from "../assets/assets";
import { riceProducts } from "../data/riceData";
import { usePopularProducts } from "../hooks/useProducts";
import FreeDeliveryBanner from "../components/FreeDeliveryBanner";

const Home = () => {
  const [currentBanner, setCurrentBanner] = useState(0);
  const banners = [
    assets.banner1,
    assets.banner2,
    assets.banner3,
    assets.banner4,
    assets.offer_banner,
  ];

  // Use React Query for popular products - automatic caching
  const {
    data: popularProducts = [],
    isLoading: loadingPopular,
    error,
  } = usePopularProducts();

  // Fallback to static data if API fails
  const displayProducts = error ? riceProducts.slice(0, 4) : popularProducts;

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentBanner((prev) => (prev + 1) % banners.length);
    }, 4000);
    return () => clearInterval(timer);
  }, [banners.length]);

  useEffect(() => {
    if (error) {
      console.error("Failed to fetch popular products:", error);
    }
  }, [error]);

  const nextBanner = () => {
    setCurrentBanner((prev) => (prev + 1) % banners.length);
  };

  const prevBanner = () => {
    setCurrentBanner((prev) => (prev - 1 + banners.length) % banners.length);
  };

  return (
    <div className="min-h-screen">
      {/* Banner Carousel Section */}
      <section className="relative w-full bg-gray-100 overflow-hidden">
        <Link to="/shop" className="block">
          <div className="relative w-full h-[300px] sm:h-[400px] md:h-[500px] lg:h-[600px] cursor-pointer">
            {banners.map((banner, index) => (
              <div
                key={index}
                className={`absolute inset-0 transition-opacity duration-700 ${
                  index === currentBanner ? "opacity-100" : "opacity-0"
                }`}
              >
                <img
                  src={banner}
                  alt={`Banner ${index + 1}`}
                  className="w-full h-full object-center"
                />
              </div>
            ))}

            {/* Navigation Arrows */}
            <button
              onClick={(e) => {
                e.preventDefault();
                prevBanner();
              }}
              className="absolute left-2 sm:left-4 top-1/2 -translate-y-1/2 bg-white/80 hover:bg-white text-[#305669] p-2 sm:p-3 rounded-full shadow-lg transition-all duration-200 z-10"
              aria-label="Previous banner"
            >
              <ChevronLeft className="h-5 w-5 sm:h-6 sm:w-6" />
            </button>
            <button
              onClick={(e) => {
                e.preventDefault();
                nextBanner();
              }}
              className="absolute right-2 sm:right-4 top-1/2 -translate-y-1/2 bg-white/80 hover:bg-white text-[#305669] p-2 sm:p-3 rounded-full shadow-lg transition-all duration-200 z-10"
              aria-label="Next banner"
            >
              <ChevronRight className="h-5 w-5 sm:h-6 sm:w-6" />
            </button>

            {/* Dots Indicator */}
            <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex space-x-2 z-10">
              {banners.map((_, index) => (
                <button
                  key={index}
                  onClick={(e) => {
                    e.preventDefault();
                    setCurrentBanner(index);
                  }}
                  className={`w-2 h-2 sm:w-3 sm:h-3 rounded-full transition-all duration-300 ${
                    index === currentBanner
                      ? "bg-white w-6 sm:w-8"
                      : "bg-white/50 hover:bg-white/75"
                  }`}
                  aria-label={`Go to banner ${index + 1}`}
                />
              ))}
            </div>
          </div>
        </Link>
      </section>
      {/* Features Section */}
      <section className="py-12 sm:py-16 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-8 sm:mb-12 px-4 sm:px-6 lg:px-8">
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-3 sm:mb-4">
              Why Choose Grainly?
            </h2>
            <p className="text-sm sm:text-base text-gray-600 max-w-2xl mx-auto">
              We are committed to delivering the highest quality rice with
              exceptional service
            </p>
          </div>

          {/* Mobile: Side by Side (No Scroll) */}
          <div className="md:hidden px-4">
            <div className="grid grid-cols-3 gap-2 sm:gap-3">
              <div className="text-center p-3 rounded-lg bg-[#B7E5CD]/20">
                <div className="bg-[#305669] w-10 h-10 sm:w-12 sm:h-12 rounded-full flex items-center justify-center mx-auto mb-2">
                  <Truck className="h-5 w-5 sm:h-6 sm:w-6 text-white" />
                </div>
                <h3 className="text-xs sm:text-sm font-semibold mb-1">
                  Fast Delivery
                </h3>
                <p className="text-[10px] sm:text-xs text-gray-600 leading-tight">
                  Same day delivery within 10km
                </p>
              </div>
              <div className="text-center p-3 rounded-lg bg-[#B7E5CD]/20">
                <div className="bg-[#305669] w-10 h-10 sm:w-12 sm:h-12 rounded-full flex items-center justify-center mx-auto mb-2">
                  <Shield className="h-5 w-5 sm:h-6 sm:w-6 text-white" />
                </div>
                <h3 className="text-xs sm:text-sm font-semibold mb-1">
                  Quality Assured
                </h3>
                <p className="text-[10px] sm:text-xs text-gray-600 leading-tight">
                  Premium rice from trusted farmers
                </p>
              </div>
              <div className="text-center p-3 rounded-lg bg-[#B7E5CD]/20">
                <div className="bg-[#305669] w-10 h-10 sm:w-12 sm:h-12 rounded-full flex items-center justify-center mx-auto mb-2">
                  <Clock className="h-5 w-5 sm:h-6 sm:w-6 text-white" />
                </div>
                <h3 className="text-xs sm:text-sm font-semibold mb-1">
                  Fresh Stock
                </h3>
                <p className="text-[10px] sm:text-xs text-gray-600 leading-tight">
                  Regular fresh stock updates
                </p>
              </div>
            </div>
          </div>

          {/* Desktop: Grid Layout */}
          <div className="hidden md:block px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-3 gap-8">
              <div className="text-center p-6 rounded-lg bg-[#B7E5CD]/20 hover:bg-[#B7E5CD]/30 transition-colors duration-200">
                <div className="bg-[#305669] w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Truck className="h-8 w-8 text-white" />
                </div>
                <h3 className="text-xl font-semibold mb-2">Fast Delivery</h3>
                <p className="text-gray-600">
                  Same day delivery within 10km radius. Fresh rice delivered to
                  your doorstep.
                </p>
              </div>
              <div className="text-center p-6 rounded-lg bg-[#B7E5CD]/20 hover:bg-[#B7E5CD]/30 transition-colors duration-200">
                <div className="bg-[#305669] w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Shield className="h-8 w-8 text-white" />
                </div>
                <h3 className="text-xl font-semibold mb-2">Quality Assured</h3>
                <p className="text-gray-600">
                  Premium quality rice sourced directly from trusted farmers
                  across India.
                </p>
              </div>
              <div className="text-center p-6 rounded-lg bg-[#B7E5CD]/20 hover:bg-[#B7E5CD]/30 transition-colors duration-200">
                <div className="bg-[#305669] w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Clock className="h-8 w-8 text-white" />
                </div>
                <h3 className="text-xl font-semibold mb-2">Fresh Stock</h3>
                <p className="text-gray-600">
                  Regular fresh stock updates. Always get the freshest rice
                  varieties.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Promotional Video Section */}
      <section className="py-12 sm:py-16 bg-gradient-to-br from-[#305669]/5 to-[#8ABEB9]/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8 sm:mb-12">
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-3 sm:mb-4">
              Discover Grainly
            </h2>
            <p className="text-sm sm:text-base text-gray-600 max-w-2xl mx-auto">
              Watch our story and see why thousands of customers trust us for
              their daily rice needs
            </p>
          </div>
          <div className="max-w-4xl mx-auto">
            <div className="relative rounded-xl overflow-hidden shadow-2xl bg-black">
              <video
                controls
                autoPlay
                muted
                loop
                playsInline
                className="w-full h-full object-cover"
                poster={assets.banner1}
              >
                <source src={assets.ads_video} type="video/mp4" />
                Your browser does not support the video tag.
              </video>
            </div>
          </div>
        </div>
      </section>

      {/* Promotional Offer Banner */}
      <FreeDeliveryBanner variant="homepage" />
      {/* Popular Products Section */}
      <section className="py-8 sm:py-12 lg:py-16 bg-gradient-to-br from-[#B7E5CD]/10 to-[#8ABEB9]/10">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="px-4 sm:px-6 lg:px-8 mb-4 sm:mb-6 lg:mb-8">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-xl sm:text-2xl lg:text-3xl font-bold text-gray-900 mb-1 sm:mb-2">
                  Popular Products
                </h2>
                <p className="text-xs sm:text-sm lg:text-base text-gray-600">
                  Our customers' favorite rice varieties
                </p>
              </div>
              <Link
                to="/shop"
                className="hidden sm:inline-flex items-center text-[#305669] hover:text-[#1785A] font-medium text-sm lg:text-base"
              >
                View All
                <ArrowRight className="ml-1 h-4 w-4" />
              </Link>
            </div>
          </div>

          {loadingPopular ? (
            <div className="flex justify-center items-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#305669]"></div>
            </div>
          ) : displayProducts.length === 0 ? (
            <div className="text-center py-12 px-4">
              <p className="text-gray-600">
                No popular products available at the moment.
              </p>
              <Link
                to="/shop"
                className="inline-flex items-center mt-4 text-[#305669] hover:text-[#1785A] font-medium"
              >
                Browse All Products
                <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
            </div>
          ) : (
            <>
              {/* Mobile: Horizontal Scroll */}
              <div className="lg:hidden overflow-x-auto scrollbar-hide px-4">
                <div className="flex gap-3 pb-4">
                  {displayProducts.map((product) => (
                    <div
                      key={product._id || product.id}
                      className="flex-shrink-0 w-[160px] sm:w-[180px] bg-white rounded-lg shadow-md overflow-hidden"
                    >
                      <Link to={`/product/${product._id || product.id}`}>
                        <div className="relative">
                          <img
                            src={product.image}
                            alt={product.name}
                            className="w-full h-[160px] sm:h-[180px] object-cover"
                          />
                          {product.discount > 0 && (
                            <div className="absolute top-1.5 right-1.5 bg-red-500 text-white px-1.5 py-0.5 rounded text-[10px] font-semibold">
                              {product.discount}% OFF
                            </div>
                          )}
                          <div className="absolute top-1.5 left-1.5 bg-yellow-500 text-white px-1.5 py-0.5 rounded text-[10px] font-semibold flex items-center gap-0.5">
                            <Star className="w-2.5 h-2.5 fill-current" />
                            POPULAR
                          </div>
                        </div>
                      </Link>
                      <div className="p-3">
                        <Link to={`/product/${product._id || product.id}`}>
                          <h3 className="text-sm font-semibold text-gray-900 mb-1 line-clamp-2 min-h-[2.5rem] hover:text-[#305669]">
                            {product.name}
                          </h3>
                        </Link>
                        <p className="text-[10px] text-gray-500 mb-2 line-clamp-1">
                          {product.weight}
                        </p>
                        <div className="flex items-center gap-1 mb-2">
                          <div className="flex items-center bg-green-600 text-white px-1 py-0.5 rounded text-[10px] font-medium">
                            <span>4.8</span>
                            <Star className="h-2 w-2 fill-current ml-0.5" />
                          </div>
                        </div>
                        <div className="flex flex-col gap-1">
                          <div className="flex items-baseline gap-1">
                            <span className="text-base font-bold text-[#305669]">
                              ₹{product.price}
                            </span>
                            {product.originalPrice &&
                              product.originalPrice > product.price && (
                                <span className="text-[10px] text-gray-400 line-through">
                                  ₹{product.originalPrice}
                                </span>
                              )}
                          </div>
                          <Link
                            to={`/product/${product._id || product.id}`}
                            onClick={() => window.scrollTo(0, 0)}
                            className="w-full bg-[#305669] text-white py-1.5 px-2 rounded text-xs font-medium text-center hover:bg-[#1785A] transition-colors"
                          >
                            View
                          </Link>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Desktop: Grid Layout */}
              <div className="hidden lg:block px-8">
                <div className="grid grid-cols-4 gap-6">
                  {displayProducts.slice(0, 4).map((product) => (
                    <div
                      key={product._id || product.id}
                      className="bg-white rounded-lg shadow-md hover:shadow-xl transition-all duration-300 overflow-hidden transform hover:-translate-y-1"
                    >
                      <Link to={`/product/${product._id || product.id}`}>
                        <div className="relative cursor-pointer">
                          <img
                            src={product.image}
                            alt={product.name}
                            className="w-full h-48 object-cover hover:scale-105 transition-transform duration-300"
                          />
                          {product.discount > 0 && (
                            <div className="absolute top-2 right-2 bg-red-500 text-white px-2 py-1 rounded-full text-xs font-semibold">
                              {product.discount}% OFF
                            </div>
                          )}
                          <div className="absolute top-2 left-2 bg-yellow-500 text-white px-2 py-1 rounded-full text-xs font-semibold flex items-center gap-1">
                            <Star className="w-3 h-3 fill-current" />
                            POPULAR
                          </div>
                        </div>
                      </Link>
                      <div className="p-6">
                        <Link to={`/product/${product._id || product.id}`}>
                          <h3 className="text-lg font-semibold text-gray-900 mb-2 hover:text-[#305669] transition-colors cursor-pointer line-clamp-2 min-h-[3.5rem]">
                            {product.name}
                          </h3>
                        </Link>
                        <p className="text-gray-600 text-sm mb-3 line-clamp-1">
                          {product.features && product.features[0]} •{" "}
                          {product.weight}
                        </p>
                        <div className="flex items-center justify-between mb-4">
                          <div className="flex items-center space-x-2">
                            <span className="text-xl font-bold text-[#305669]">
                              ₹{product.price}
                            </span>
                            {product.originalPrice &&
                              product.originalPrice > product.price && (
                                <span className="text-sm text-gray-500 line-through">
                                  ₹{product.originalPrice}
                                </span>
                              )}
                          </div>
                          <div className="flex items-center">
                            {[...Array(5)].map((_, i) => (
                              <Star
                                key={i}
                                className="h-4 w-4 fill-current text-yellow-400"
                              />
                            ))}
                          </div>
                        </div>
                        <Link
                          to={`/product/${product._id || product.id}`}
                          onClick={() => window.scrollTo(0, 0)}
                          className="w-full bg-[#305669] text-white py-2 px-4 rounded-lg hover:bg-[#1785A] transition-colors duration-200 text-center block font-medium"
                        >
                          View Details
                        </Link>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </>
          )}

          {/* View All Button - Mobile Only */}
          <div className="text-center mt-6 px-4 sm:hidden">
            <Link
              to="/shop"
              className="inline-flex items-center bg-[#305669] text-white px-6 py-2 rounded-lg font-semibold hover:bg-[#1785A] transition-colors duration-200 text-sm"
            >
              View All Products
              <ArrowRight className="ml-2 h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>
      {/* Our Culture Section */}
      <section className="py-12 sm:py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8 sm:mb-12">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 mb-3 sm:mb-4">
              Our Culture
            </h2>
            <p className="text-sm sm:text-base text-gray-600">
              Celebrating the rich heritage of rice farming
            </p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4 md:gap-6">
            <div className="group relative overflow-hidden rounded-lg shadow-md hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1">
              <img
                src={assets.collection1}
                alt="Rice Collection 1"
                className="w-full h-40 sm:h-48 md:h-56 lg:h-64 object-cover group-hover:scale-110 transition-transform duration-500"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                <div className="absolute bottom-0 left-0 right-0 p-3 sm:p-4 text-white">
                  <p className="text-xs sm:text-sm font-semibold">
                    Premium Quality
                  </p>
                </div>
              </div>
            </div>
            <div className="group relative overflow-hidden rounded-lg shadow-md hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1">
              <img
                src={assets.collection2}
                alt="Rice Collection 2"
                className="w-full h-40 sm:h-48 md:h-56 lg:h-64 object-cover group-hover:scale-110 transition-transform duration-500"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                <div className="absolute bottom-0 left-0 right-0 p-3 sm:p-4 text-white">
                  <p className="text-xs sm:text-sm font-semibold">
                    Fresh & Aromatic
                  </p>
                </div>
              </div>
            </div>
            <div className="group relative overflow-hidden rounded-lg shadow-md hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1">
              <img
                src={assets.collection3}
                alt="Rice Collection 3"
                className="w-full h-40 sm:h-48 md:h-56 lg:h-64 object-cover group-hover:scale-110 transition-transform duration-500"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                <div className="absolute bottom-0 left-0 right-0 p-3 sm:p-4 text-white">
                  <p className="text-xs sm:text-sm font-semibold">
                    Authentic Taste
                  </p>
                </div>
              </div>
            </div>
            <div className="group relative overflow-hidden rounded-lg shadow-md hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1">
              <img
                src={assets.collection4}
                alt="Rice Collection 4"
                className="w-full h-40 sm:h-48 md:h-56 lg:h-64 object-cover group-hover:scale-110 transition-transform duration-500"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                <div className="absolute bottom-0 left-0 right-0 p-3 sm:p-4 text-white">
                  <p className="text-xs sm:text-sm font-semibold">Farm Fresh</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};
export default Home;
